/* eslint-disable import/no-cycle */
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Pagination } from '@mantine/core';
import WsProductCard from './WsProductCard';
import AppStack from '../../../../../common/AppStack';
import AppFlexbox from '../../../../../common/AppFlexbox';
import AppText from '../../../../../common/AppText';
import WsProductGridFilter from './WsProductGridFilter';
import { useWebstoreSearchFilter } from '../../../../../../helpers/shop/webstoreHelper';
import { Context as StoreUserContext } from '../../../../../../providers/StoreUserContextProvider';
import { useMediaQueries } from '../../../../../../helpers/hooks';

const PRODUCT_SORT_OPTIONS = [
  {
    label: 'Featured',
    value: 'featured'
  },
  {
    label: 'Best selling',
    value: 'best_selling'
  },
  {
    label: 'Alphabetically, A-Z',
    value: 'name'
  },
  {
    label: 'Alphabetically, Z-A',
    value: 'name_desc'
  },
  {
    label: 'Price, low to high',
    value: 'price'
  },
  {
    label: 'Price, high to low',
    value: 'price_desc'
  },
  {
    label: 'Date, old to new',
    value: 'created'
  },
  {
    label: 'Date, new to old',
    value: 'created_desc'
  }
];

const QUICK_ADD_STYLE_ENUM = {
  NONE: 'NONE',
  STANDARD: 'STANDARD',
  BULK: 'BULK'
};

const DESKTOP_FILTER_LAYOUT_ENUM = {
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  DRAWER: 'DRAWER'
};

const IMAGE_DISPLAY_ENUM = {
  PORTRAIT: 'PORTRAIT',
  SQUARE: 'SQUARE'
};

const WsProductGrid = ({
  config,
  themeConfig,
  colorSchemes,
  mediaQueries,
  pathname,
  pkEcomStore,
  onNavigate,
  baseStoreUrl,
  editModeEnabled,
  search,
  pkRegAssociation,
  pkEcomStoreCollection,
  analyticCta,
  ...rest
}) => {
  const fetchedPageSize = useRef(null);
  const [debouncedPageSize, setDebouncedPageSize] = useState(
    config.productsPerPage * 1 || 20
  );
  const [filterParamsState, setFilterParamsState] = useState({
    searchParam: ''
  });
  const { filter } = useWebstoreSearchFilter({
    search: filterParamsState.searchParam,
    setFilterStateInstance: setFilterParamsState
  });
  const [pageIndexState, setPageIndexState] = useState(null);
  const [filterState, setFilterState] = useState({});
  const { state, fetchEcomStoreProducts } = useContext(StoreUserContext);
  const { isTabletOrSmaller, isLaptopOrSmaller } =
    mediaQueries || useMediaQueries();
  const colorScheme = colorSchemes[config.colorScheme - 1] || colorSchemes[0];

  const {
    pageIndex,
    totalPages,
    filter: dataFilter,
    data: productData,
    loading: dataLoading
  } = state.paginatedEcomStoreProducts;

  const showSecondImageOnHover = config.showSecondImageOnHover === 'true';
  const showVendorName = config.showVendorName === 'true';
  const squareRatio = config.imageRatio === IMAGE_DISPLAY_ENUM.SQUARE;
  const productsPerPage = config.productsPerPage * 1 || 20;
  const isQuickAddEnabled =
    config.quickAddStyle === QUICK_ADD_STYLE_ENUM.STANDARD;

  const alreadyLoaded =
    dataFilter &&
    dataFilter.fkEcomStoreCollection === (pkEcomStoreCollection || null);
  const isVerticalFilter =
    config.filterLayout === DESKTOP_FILTER_LAYOUT_ENUM.VERTICAL;
  const loading = !alreadyLoaded || dataLoading;
  const filterEnabled = config.filterEnabled === 'true';

  const hideAllProductBadges = themeConfig.hideAllProductBadges === 'true';
  const hideFreeShippingBadge = themeConfig.hideFreeShippingBadge === 'true';

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPageSize(productsPerPage);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [productsPerPage]);

  useEffect(() => {
    if (
      fetchedPageSize.current !== debouncedPageSize ||
      (!alreadyLoaded && pkEcomStoreCollection) ||
      Object.keys(filter).some((key) => filter[key] !== filterState[key])
    ) {
      // eslint-disable-next-line no-use-before-define
      fetchStoreProducts();
      setFilterState(filter);
      fetchedPageSize.current = debouncedPageSize;
    }
  }, [pkEcomStoreCollection, filter, debouncedPageSize]);

  const fetchStoreProducts = (nextIndex = 1) => {
    setPageIndexState(nextIndex);
    fetchEcomStoreProducts(
      pkEcomStore,
      {
        ...filter,
        fkEcomStoreCollection: pkEcomStoreCollection || null,
        pageSize: productsPerPage,
        sort: filter.sort || PRODUCT_SORT_OPTIONS[0].value,
        page: nextIndex
      },
      () => {},
      () => {
        setPageIndexState(null);
      }
    );
    if (nextIndex > 1) {
      const scrollContainerRef = document.getElementById(
        'product-grid-container'
      );
      if (scrollContainerRef) {
        scrollContainerRef.scrollIntoView({ block: 'start' });
      }
    }
  };

  return (
    <AppStack style={{ width: '100%' }}>
      <WsProductGridFilter
        baseStoreUrl={baseStoreUrl}
        colorSchemes={colorSchemes}
        config={config}
        editModeEnabled={editModeEnabled}
        loading={loading}
        mediaQueries={mediaQueries}
        onNavigate={onNavigate}
        pathname={pathname}
        pkEcomStore={pkEcomStore}
        search={filterParamsState.searchParam}
        setFilterStateInstance={setFilterParamsState}
        sortOptions={PRODUCT_SORT_OPTIONS}
        themeConfig={themeConfig}
        {...rest}
      />

      <AppFlexbox style={{ flex: 1, width: '100%', justifyContent: 'center' }}>
        {!isTabletOrSmaller && filterEnabled && isVerticalFilter && (
          <WsProductGridFilter
            baseStoreUrl={baseStoreUrl}
            colorSchemes={colorSchemes}
            config={config}
            editModeEnabled={editModeEnabled}
            mediaQueries={mediaQueries}
            onNavigate={onNavigate}
            pathname={pathname}
            pkEcomStore={pkEcomStore}
            search={filterParamsState.searchParam}
            setFilterStateInstance={setFilterParamsState}
            sortOptions={PRODUCT_SORT_OPTIONS}
            themeConfig={themeConfig}
            verticalLayout
            {...rest}
          />
        )}

        <AppStack style={{ flex: 1 }}>
          {!loading ? (
            productData.length === 0 ? (
              <AppStack style={{ flex: 1, alignItems: 'center', padding: 40 }}>
                <AppText
                  style={{
                    color: colorScheme.textColor,
                    fontSize: 18,
                    fontWeight: '500'
                  }}
                >
                  No products found
                </AppText>
              </AppStack>
            ) : (
              <Grid
                columns={
                  (
                    filterEnabled && isVerticalFilter
                      ? isLaptopOrSmaller
                      : isTabletOrSmaller
                  )
                    ? config.mobileColumnCount * 1 || 1
                    : config.desktopColumnCount * 1 || 6
                }
                style={{ flex: 1 }}
              >
                {productData.slice(0, productsPerPage).map((p) => (
                  <Grid.Col key={p.pkEcomStoreProduct} span={1}>
                    <WsProductCard
                      analyticCta={analyticCta}
                      baseStoreUrl={baseStoreUrl}
                      colorScheme={colorScheme}
                      ecomStoreProduct={p}
                      hideAllProductBadges={hideAllProductBadges}
                      hideFreeShippingBadge={hideFreeShippingBadge}
                      isQuickAddEnabled={isQuickAddEnabled}
                      onNavigate={onNavigate}
                      portraitRatio={!squareRatio}
                      showSecondImageOnHover={showSecondImageOnHover}
                      showVendorName={showVendorName}
                      url={
                        pkRegAssociation
                          ? `/association/${pkRegAssociation}?urlHandle=${p.urlHandle}`
                          : null
                      }
                    />
                  </Grid.Col>
                ))}
              </Grid>
            )
          ) : (
            <Grid
              columns={
                (
                  filterEnabled && isVerticalFilter
                    ? isLaptopOrSmaller
                    : isTabletOrSmaller
                )
                  ? config.mobileColumnCount * 1 || 1
                  : config.desktopColumnCount * 1 || 6
              }
              style={{ flex: 1 }}
            >
              {Array.from(Array(productsPerPage)).map((x, i) => (
                <Grid.Col
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  span={1}
                >
                  <WsProductCard
                    analyticCta={analyticCta}
                    colorScheme={colorScheme}
                    hideAllProductBadges={hideAllProductBadges}
                    hideFreeShippingBadge={hideFreeShippingBadge}
                    isQuickAddEnabled={isQuickAddEnabled}
                    onNavigate={onNavigate}
                    portraitRatio={!squareRatio}
                    showSecondImageOnHover={showSecondImageOnHover}
                    showVendorName={showVendorName}
                  />
                </Grid.Col>
              ))}
            </Grid>
          )}

          {totalPages > 1 && (
            <AppFlexbox style={{ justifyContent: 'center' }}>
              <AppStack>
                <Pagination
                  color="dark"
                  onChange={fetchStoreProducts}
                  total={totalPages}
                  value={pageIndexState || pageIndex}
                />
              </AppStack>
            </AppFlexbox>
          )}
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

WsProductGrid.propTypes = {
  analyticCta: PropTypes.string,
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  pkEcomStore: PropTypes.number,
  pkEcomStoreCollection: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  pkRegAssociation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resourceNavigation: PropTypes.array,
  search: PropTypes.string,
  selectedResources: PropTypes.object,
  themeConfig: PropTypes.object
};

export default WsProductGrid;
